//@flow
import React, {useEffect} from 'react';
import {makeStyles} from '@material-ui/core/styles';
import {
  Grid,
  Typography,
  Icon,
  RadioGroup,
  FormControlLabel,
  Radio,
} from '@material-ui/core';
import Autocomplete from '@material-ui/lab/Autocomplete';
import {Controller, useFormContext} from 'react-hook-form';
import CustomTextField from '../../../../elements/CustomTextField/CustomTextField';
import CircularProgress from '@material-ui/core/CircularProgress';
import {red, green} from '@material-ui/core/colors';
import {
  Error as ErrorIcon,
  CheckCircle as CheckCircleIcon,
} from '@material-ui/icons';

import type {ContactType, Store, StoreType} from '../../../../entities';
import type {Control, Ref, ValidationRules} from 'react-hook-form';
import {ON_BOARDING_FIELDS} from '../../utils/constants';

type Props = {|
  store: Store,
  contactTypes: ContactType[],
  isValidating?: boolean,
  storeTypes?: StoreType[],
  subdomainRegister: (rules?: ValidationRules) => (ref: Ref | null) => void,
  subdomainError?: {[key: string]: any},
  validationResult?: boolean,
  errors?: {[key: string]: {[key: string]: string}},
  trigger: (field?: string) => void,
  control: Control,
  setStoreTypeSelected: (value: string) => void,
  getValues: (field: string) => string,
|};

const useStyles = makeStyles(theme => ({
  domainWrap: {
    position: 'relative',
  },
  subdomainInput: {},
  rootDomain: {
    position: 'absolute',
    right: theme.spacing(3),
    top: 32,
    fontWeight: 'bold',
  },
  indicatorSuccess: {
    marginTop: theme.spacing(1),
    color: green['500'],
  },
  indicatorError: {
    marginTop: theme.spacing(1),
    color: red['500'],
  },
  imageBoxDashed: {
    border: `1px dashed ${theme.palette.primary.main}`,
    boxSizing: 'border-box',
    borderRadius: theme.borderRadius,
    padding: theme.spacing(4, 2),
    textAlign: 'center',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  imageBox: {
    border: '1px solid #E8E8E9',
    boxSizing: 'border-box',
    borderRadius: theme.borderRadius,
    padding: theme.spacing(2),
    textAlign: 'center',
    position: 'relative',
    minHeight: 82,
  },
  flagIcon: {
    width: 20,
    height: 20,
    marginLeft: 6,
    marginTop: 2,
    borderRadius: '100%',
    verticalAlign: 'sub',
  },
  currencyInnerInput: {
    '& input': {
      paddingRight: '50px!important',
    },
  },
  divider: {
    border: '1px solid #E8E8ED',
    margin: '16px auto',
  },
  sectionTitle: {
    fontWeight: 600,
    fontSize: '1.25rem',
  },
  radioGroup: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(2),
  },
}));
type Option = {
  label: string,
  value: string,
  description: string,
};

const options: Option[] = [
  {
    label: 'Productos',
    value: 'PRODUCT',
    description: 'Productos físicos o digitales, como ropa, accesorios, etc.',
  },
  {
    label: 'Servicios',
    value: 'SERVICE',
    description: 'Servicios como clases, asesorías, etc.',
  },
];

export default function StoreConfig(props: Props) {
  const {
    store,
    subdomainRegister,
    validationResult,
    storeTypes,
    errors = {},
    trigger,
    control,
    setStoreTypeSelected,
    // getValues,
    isValidating,
  } = props;
  const classes = useStyles();

  const {register, setValue, watch, clearErrors} = useFormContext();

  useEffect(() => {
    if (typeof validationResult === 'boolean') trigger('subdomain');
  }, [trigger, validationResult]);

  useEffect(() => {
    register(ON_BOARDING_FIELDS.STORE_BUSINESS_TYPE, {
      required: 'Este campo es requerido',
    });
  }, [register]);

  const storeBusinessType = watch(ON_BOARDING_FIELDS.STORE_BUSINESS_TYPE);

  const error = errors[ON_BOARDING_FIELDS.STORE_BUSINESS_TYPE];

  return (
    <div className={classes.formWrapper}>
      <Grid container spacing={3}>
        <Grid item xs={12} sm={12}>
          <CustomTextField
            label="Nombre de tienda"
            fullWidth
            name="name"
            error={!!errors.name}
            helperText={errors.name ? errors.name.message : ''}
            inputRef={subdomainRegister({
              required: 'Campo obligatorio',
              pattern: {
                value: /[A-Za-z0-9_ ]+/,
                message:
                  'El nombre de la tienda no puede contener caracteres especiales como “!%$&”, ni menos de 2 caracteres',
              },
              minLength: 3,
            })}
            defaultValue={store && store.name}
          />
          <Grid
            container
            item
            justify="flex-start"
            alignItems="center"
            className={`${validationResult ? classes.indicatorSuccess : ''} ${
              !validationResult ? classes.indicatorError : ''
            }`}
          >
            {isValidating ? (
              <>
                <Icon>
                  <CircularProgress size={20} />
                </Icon>
                <Typography variant="body1" align="left" color="primary">
                  Validando disponibilidad del nombre de tienda...
                </Typography>
              </>
            ) : (
              <>
                <Icon>
                  {validationResult && <CheckCircleIcon />}
                  {!validationResult && <ErrorIcon />}
                </Icon>
                <Typography variant="body1" align="left">
                  {!validationResult
                    ? 'Este nombre ya está registrado'
                    : 'El nombre de tienda está disponible '}
                </Typography>
              </>
            )}
          </Grid>
        </Grid>
        {!!storeTypes ? (
          <Grid item xs={12} sm={12} className={classes.domainWrap}>
            <Controller
              control={control}
              name="storeType"
              defaultValue={!!store.storeType ? store.storeType.id : null}
              render={({onChange, value}) => (
                <Autocomplete
                  name="storeType"
                  value={value}
                  ref={subdomainRegister}
                  options={storeTypes}
                  getOptionLabel={option => {
                    if (typeof option === 'string') {
                      const t = storeTypes.find(s => s.id === option);
                      return t ? t.label : '';
                    } else {
                      return option.label;
                    }
                  }}
                  filterSelectedOptions
                  getOptionSelected={(option, selectedValue) =>
                    option.id === selectedValue
                  }
                  onChange={(_, value) => {
                    setStoreTypeSelected(value.id);
                    return onChange(value.id);
                  }}
                  disableClearable
                  renderInput={params => (
                    <CustomTextField
                      {...params}
                      name="storeType"
                      label="Rubro de tienda"
                      error={!!errors.storeType}
                      helperText={
                        errors.storeType ? errors.storeType.message : ''
                      }
                      inputRef={subdomainRegister({
                        required: 'Campo obligatorio',
                      })}
                    />
                  )}
                />
              )}
            />
          </Grid>
        ) : null}

        <Grid item xs={12} sm={12} className={classes.domainWrap}>
          <Typography
            component="span"
            variant="h6"
            className={classes.sectionTitle}
          >
            Tipo de tienda
          </Typography>
          <RadioGroup className={classes.radioGroup} error={error}>
            {options.map(option => (
              <FormControlLabel
                key={option.value}
                onClick={() => {
                  setValue(
                    ON_BOARDING_FIELDS.STORE_BUSINESS_TYPE,
                    option.value
                  );
                  clearErrors(ON_BOARDING_FIELDS.STORE_BUSINESS_TYPE);
                }}
                checked={storeBusinessType === option.value}
                control={<Radio color="primary" />}
                label={
                  <div>
                    <Typography
                      className={classes.nameEstado}
                      color={!!error ? 'error' : 'inherit'}
                    >
                      {option.label}
                    </Typography>
                    <Typography
                      className={classes.nameEstado}
                      variant="caption"
                      color={!!error ? 'error' : 'inherit'}
                    >
                      {option.description}
                    </Typography>
                  </div>
                }
                value={option.value}
              />
            ))}
          </RadioGroup>
        </Grid>
      </Grid>
    </div>
  );
}
